import { useEffect, useState } from "react";
import { api, formatRupiah } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Banknote, Eye, CheckCircle2 } from "lucide-react";
import { PageLoader } from "@/components/PageLoader";

const STATUS_MAP = {
  active: ["bg-blue-100 text-blue-800 border-blue-300", "Berjalan"],
  paid_off: ["bg-green-100 text-green-800 border-green-300", "Lunas"],
  cancelled: ["bg-zinc-100 text-zinc-700 border-zinc-300", "Dibatalkan"],
};

const fmtMonth = (ym) => {
  if (!ym) return "—";
  const [y, m] = ym.split("-");
  const bln = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agt", "Sep", "Okt", "Nov", "Des"];
  return `${bln[parseInt(m) - 1] || m} ${y}`;
};

export default function EmployeeLoans() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get("/loans/me")
      .then((r) => { if (!cancel) setList(r.data); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, []);

  if (loading) return <PageLoader label="Memuat pinjaman..." />;

  const active = list.filter((l) => l.status === "active");
  const totalOutstanding = active.reduce((s, l) => s + l.outstanding, 0);
  const totalMonthly = active.reduce((s, l) => s + Math.min(l.monthly_minimum, l.outstanding), 0);

  return (
    <div className="px-4 py-6 pb-24 max-w-md mx-auto" data-testid="employee-loans-page">
      <p className="overline">Transparansi</p>
      <h1 className="font-display text-2xl font-bold tracking-tight">Pinjaman Saya</h1>
      <p className="text-sm text-zinc-500 mt-1">
        Daftar pinjaman ke perusahaan & status cicilan
      </p>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-2 mt-4">
        <Card className="p-3 border border-zinc-200 shadow-none">
          <p className="overline text-[0.6rem] mb-1">Total Sisa Hutang</p>
          <p className="font-display text-base sm:text-lg font-bold text-amber-700 truncate" data-testid="emp-total-outstanding">
            {formatRupiah(totalOutstanding)}
          </p>
        </Card>
        <Card className="p-3 border border-zinc-200 shadow-none">
          <p className="overline text-[0.6rem] mb-1">Potongan Per Bulan</p>
          <p className="font-display text-base sm:text-lg font-bold text-zinc-800 truncate" data-testid="emp-total-monthly">
            {formatRupiah(totalMonthly)}
          </p>
        </Card>
      </div>

      {list.length === 0 ? (
        <Card className="p-12 mt-4 text-center border border-zinc-200 shadow-none">
          <Banknote className="w-10 h-10 mx-auto text-zinc-300 mb-2" />
          <p className="text-sm text-zinc-500">Anda tidak memiliki pinjaman aktif</p>
          <p className="text-xs text-zinc-400 mt-1">
            Pengajuan pinjaman dilakukan langsung ke HRD perusahaan.
          </p>
        </Card>
      ) : (
        <div className="space-y-3 mt-4">
          {list.map((loan) => (
            <LoanCardEmp key={loan.id} loan={loan} onView={() => setDetail(loan)} />
          ))}
        </div>
      )}

      {detail && <LoanDetailEmp loan={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

const LoanCardEmp = ({ loan, onView }) => {
  const [cls, label] = STATUS_MAP[loan.status] || ["", loan.status];
  const progress = loan.principal > 0 ? Math.min(100, (loan.total_paid / loan.principal) * 100) : 0;
  return (
    <Card className="p-4 border border-zinc-200 shadow-none" data-testid={`emp-loan-${loan.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
            <Badge className={`${cls} border text-[0.65rem]`} variant="outline">{label}</Badge>
            <Badge variant="outline" className="text-[0.65rem]">{loan.category_name}</Badge>
          </div>
          <h3 className="font-display font-semibold text-base truncate">{loan.title}</h3>
          <p className="text-xs text-zinc-500">Mulai {fmtMonth(loan.start_period)}</p>
        </div>
        <Button size="sm" variant="ghost" onClick={onView} data-testid={`emp-btn-view-${loan.id}`}>
          <Eye className="w-4 h-4" />
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-2 my-3 text-sm">
        <div className="bg-zinc-50 p-2 rounded">
          <p className="text-[0.65rem] text-zinc-500">Sisa</p>
          <p className="font-mono font-bold text-amber-700 text-sm">{formatRupiah(loan.outstanding)}</p>
        </div>
        <div className="bg-zinc-50 p-2 rounded">
          <p className="text-[0.65rem] text-zinc-500">Cicilan/bln</p>
          <p className="font-mono font-bold text-zinc-800 text-sm">{formatRupiah(loan.monthly_minimum)}</p>
        </div>
      </div>

      <div className="w-full bg-zinc-100 rounded-full h-1.5 overflow-hidden">
        <div
          className={`h-full transition-all ${loan.is_paid_off ? "bg-green-500" : "bg-primary"}`}
          style={{ width: `${progress}%` }}
        />
      </div>
      <p className="text-xs text-zinc-500 mt-1.5 flex items-center gap-1">
        {loan.is_paid_off && <CheckCircle2 className="w-3 h-3 text-green-600" />}
        {loan.is_paid_off
          ? "Pinjaman sudah lunas"
          : `${loan.months_paid} bulan dibayar dari ${formatRupiah(loan.principal)}`}
      </p>
    </Card>
  );
};

const LoanDetailEmp = ({ loan, onClose }) => (
  <Dialog open={true} onOpenChange={onClose}>
    <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{loan.title}</DialogTitle>
        <DialogDescription>
          {loan.category_name} · Mulai {fmtMonth(loan.start_period)}
        </DialogDescription>
      </DialogHeader>

      {loan.description && (
        <p className="text-sm text-zinc-700 mb-2">{loan.description}</p>
      )}

      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="bg-zinc-50 p-2 rounded">
          <p className="text-[0.6rem] text-zinc-500">Pokok</p>
          <p className="font-mono text-xs font-bold">{formatRupiah(loan.principal)}</p>
        </div>
        <div className="bg-green-50 p-2 rounded">
          <p className="text-[0.6rem] text-zinc-500">Dibayar</p>
          <p className="font-mono text-xs font-bold text-green-700">{formatRupiah(loan.total_paid)}</p>
        </div>
        <div className="bg-amber-50 p-2 rounded">
          <p className="text-[0.6rem] text-zinc-500">Sisa</p>
          <p className="font-mono text-xs font-bold text-amber-700">{formatRupiah(loan.outstanding)}</p>
        </div>
      </div>

      <h4 className="font-display text-sm font-semibold mb-2">Riwayat Cicilan</h4>
      {loan.schedule.length === 0 ? (
        <p className="text-sm text-zinc-500 py-3 text-center bg-zinc-50 rounded">
          Belum ada periode terlewati
        </p>
      ) : (
        <div className="border border-zinc-200 rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-50">
                <TableHead className="text-xs">Bulan</TableHead>
                <TableHead className="text-xs text-right">Dipotong</TableHead>
                <TableHead className="text-xs text-right">Sisa</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loan.schedule.map((s) => (
                <TableRow key={s.period}>
                  <TableCell className="text-xs font-mono">
                    {fmtMonth(s.period)}
                    {s.is_override && <Badge variant="outline" className="ml-1 text-[0.55rem] bg-blue-50 text-blue-700 border-blue-300">extra</Badge>}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">{formatRupiah(s.amount)}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-amber-700">{formatRupiah(s.balance_after)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </DialogContent>
  </Dialog>
);
